import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import {
  ArrowRight,
  Armchair,
  BrainCircuit,
  CalendarCheck,
  Eye,
  EyeOff,
  Loader2,
  Moon,
  ShieldCheck,
  Sun,
} from "lucide-react";
import { ApiError, getCurrentTenant } from "@eduviaos/shared";
import { Button, EduViaOSMark, Input, Label, cn, useAppearance } from "@eduviaos/ui-kit";
import type { Institution } from "../lib/institution";
import { publicApi } from "../lib/api";
import { useSession } from "../lib/sessionContext";
import { usePortalLocale } from "../lib/i18n/usePortalLocale";
import { authErrorMessage } from "../lib/apiErrorCopy";
import { InstitutionMark } from "../components/InstitutionMark";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

export function LoginScreen({ institution }: { institution: Institution }) {
  const reduce = useReducedMotion();
  const { login } = useSession();
  const { resolvedMode, set } = useAppearance();
  const { strings: s } = usePortalLocale(false);

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [magicPending, setMagicPending] = React.useState(false);
  const [magicSent, setMagicSent] = React.useState(false);
  const [useMagicLink, setUseMagicLink] = React.useState(institution.vertical === "study_centre");
  const [error, setError] = React.useState<string | null>(null);

  const emailValid = /\S+@\S+\.\S+/.test(email);
  const canSubmitPassword = emailValid && password.length > 0 && !pending;
  const canSubmitMagic = emailValid && !magicPending;

  const heroPoints =
    institution.vertical === "study_centre"
      ? [
          { icon: Armchair, text: s.loginHeroPointSeat },
          { icon: BrainCircuit, text: s.loginHeroPointReview },
        ]
      : institution.vertical === "school"
        ? [
            { icon: CalendarCheck, text: "Timetable, homework & notices in one place" },
            { icon: ShieldCheck, text: "Private & secure — just for you" },
          ]
        : [
            { icon: CalendarCheck, text: "Classes, tests & results in one place" },
            { icon: ShieldCheck, text: "Private & secure — just for you" },
          ];

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmitPassword) return;
    setError(null);
    setPending(true);
    try {
      await login({ email: email.trim(), password, tenantId: institution.slug });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(s.loginInvalidCredentials);
      } else {
        setError(authErrorMessage(err, s, s.loginFailed));
      }
      setPassword("");
    } finally {
      setPending(false);
    }
  }

  async function handleMagicSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmitMagic) return;
    setError(null);
    setMagicPending(true);
    try {
      const body: Record<string, string> = { email: email.trim() };
      if (!getCurrentTenant().slug) body.tenant_id = institution.slug;
      await publicApi.post("/auth/magic-link", body);
      setMagicSent(true);
    } catch (err) {
      setError(authErrorMessage(err, s, s.loginFailed));
    } finally {
      setMagicPending(false);
    }
  }

  function toggleMode() {
    set({ mode: resolvedMode === "dark" ? "light" : "dark" });
  }

  return (
    <div className="portal-aurora portal-grain bg-background text-foreground relative flex min-h-dvh flex-col overflow-hidden lg:flex-row">
      <FloatingShapes reduce={!!reduce} />

      <motion.aside
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, ease: EASE_OUT }}
        className="relative z-10 flex flex-col justify-between px-6 pb-2 pt-8 lg:w-[46%] lg:px-14 lg:py-12"
      >
        <div className="flex items-center gap-3">
          <InstitutionMark vertical={institution.vertical} name={institution.name} size="md" />
          <div className="leading-tight">
            <p className="font-semibold">{institution.shortName}</p>
            <p className="text-muted-foreground text-xs">
              {s.loginVerticalLabel(institution.vertical)}
            </p>
          </div>
        </div>

        <div className="hidden max-w-md py-10 lg:block">
          <motion.h1
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: EASE_OUT, delay: 0.05 }}
            className="font-display text-[2.9rem] font-semibold leading-[1.05] tracking-tight"
          >
            {s.loginHeroHeadline1}
            <br />
            {s.loginHeroHeadline2}
          </motion.h1>
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: EASE_OUT, delay: 0.12 }}
            className="text-muted-foreground mt-4 text-lg"
          >
            {s.loginTagline(institution.vertical)}
          </motion.p>

          <ul className="mt-8 space-y-3">
            {heroPoints.map((point, i) => (
              <motion.li
                key={point.text}
                initial={reduce ? false : { opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, ease: EASE_OUT, delay: 0.2 + i * 0.07 }}
                className="flex items-center gap-3 text-sm font-medium"
              >
                <span className="bg-primary/12 text-primary grid size-8 place-items-center rounded-full">
                  <point.icon className="size-4" />
                </span>
                {point.text}
              </motion.li>
            ))}
          </ul>
        </div>

        <p className="text-muted-foreground hidden items-center gap-1.5 text-xs lg:inline-flex">
          <EduViaOSMark className="size-3.5" />
          {s.loginPoweredBy}
        </p>
      </motion.aside>

      <main className="relative z-10 flex flex-1 items-center justify-center px-5 pb-10 pt-4 lg:pt-12">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 18, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: EASE_OUT, delay: 0.08 }}
          className="border-border/70 bg-card/80 w-full max-w-[26rem] rounded-[calc(var(--radius-2xl))] border p-6 shadow-[var(--shadow-lg)] backdrop-blur-xl sm:p-8"
        >
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h2 className="font-display text-2xl font-semibold tracking-tight">
                {s.loginWelcome}
              </h2>
              <p className="text-muted-foreground mt-1 text-sm">
                {s.loginSubtitle(institution.shortName)}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={toggleMode}
              aria-label={
                resolvedMode === "dark" ? s.loginSwitchToLightMode : s.loginSwitchToDarkMode
              }
              className="text-muted-foreground rounded-full"
            >
              {resolvedMode === "dark" ? <Sun className="size-5" /> : <Moon className="size-5" />}
            </Button>
          </div>

          {useMagicLink && institution.vertical === "study_centre" ? (
            <form onSubmit={handleMagicSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{s.loginEmail}</Label>
                <Input
                  id="email"
                  type="email"
                  autoFocus
                  inputMode="email"
                  autoComplete="username"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl text-base"
                />
              </div>
              {magicSent ? (
                <p className="bg-primary/10 text-foreground rounded-lg px-3 py-3 text-sm leading-relaxed">
                  {s.loginMagicLinkSent}
                </p>
              ) : (
                <Button
                  type="submit"
                  disabled={!canSubmitMagic}
                  className="h-12 w-full rounded-xl text-base font-semibold transition-transform duration-150 ease-out active:scale-[0.98]"
                >
                  {magicPending ? (
                    <>
                      <Loader2 className="size-5 animate-spin" />
                      {s.loginMagicLinkPending}
                    </>
                  ) : (
                    <>
                      {s.loginMagicLinkCta}
                      <ArrowRight className="size-4" />
                    </>
                  )}
                </Button>
              )}
              <button
                type="button"
                className="text-primary w-full text-center text-sm font-medium underline-offset-4 hover:underline"
                onClick={() => {
                  setUseMagicLink(false);
                  setMagicSent(false);
                  setError(null);
                }}
              >
                {s.loginUsePassword}
              </button>
            </form>
          ) : (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{s.loginEmail}</Label>
                <Input
                  id="email"
                  type="email"
                  autoFocus
                  inputMode="email"
                  autoComplete="username"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl text-base"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{s.loginPassword}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder={s.loginPasswordPlaceholder}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 rounded-xl pr-12 text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? s.loginHidePassword : s.loginShowPassword}
                    className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 grid w-12 place-items-center rounded-r-xl transition-colors"
                  >
                    {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={!canSubmitPassword}
                className="h-12 w-full rounded-xl text-base font-semibold transition-transform duration-150 ease-out active:scale-[0.98]"
              >
                {pending ? (
                  <>
                    <Loader2 className="size-5 animate-spin" aria-hidden="true" />
                    {s.loginSigningIn}
                  </>
                ) : (
                  <>
                    {s.loginSignIn}
                    <ArrowRight className="size-4" />
                  </>
                )}
              </Button>

              {institution.vertical === "study_centre" ? (
                <button
                  type="button"
                  className="text-primary w-full text-center text-sm font-medium underline-offset-4 hover:underline"
                  onClick={() => {
                    setUseMagicLink(true);
                    setError(null);
                  }}
                >
                  {s.loginUseMagicLink}
                </button>
              ) : null}
            </form>
          )}

          {error && (
            <motion.p
              initial={reduce ? false : { opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18 }}
              role="alert"
              className="bg-destructive/10 text-destructive mt-4 rounded-lg px-3 py-2 text-center text-sm font-medium"
            >
              {error}
            </motion.p>
          )}

          <p className="text-muted-foreground mt-5 text-center text-xs">
            {s.loginTrouble(s.loginHelpNoun(institution.vertical))}
          </p>
        </motion.div>
      </main>
    </div>
  );
}

function FloatingShapes({ reduce }: { reduce: boolean }) {
  const blobs = [
    { className: "left-[-6rem] top-[8%] size-64 bg-primary/25", delay: 0 },
    { className: "right-[-4rem] top-[40%] size-52 bg-celebrate/20", delay: 1.2 },
    { className: "bottom-[-5rem] left-[30%] size-72 bg-primary/15", delay: 0.6 },
  ];
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {blobs.map((b, i) => (
        <motion.span
          key={i}
          className={cn("absolute rounded-full blur-3xl", b.className)}
          animate={
            reduce
              ? undefined
              : { transform: ["translateY(0px)", "translateY(-26px)", "translateY(0px)"] }
          }
          transition={{ duration: 9 + i * 2, repeat: Infinity, ease: "easeInOut", delay: b.delay }}
        />
      ))}
    </div>
  );
}
